import Highcharts from "highcharts"

export interface ChartSeries {
  name: string
  data: number[]
  color: string
}

export const getStackedColumnChartOptions = (
  categories: string[],
  series: ChartSeries[],
  viewType: "daily" | "monthly",
  onPointClick?: (index: number, seriesName: string) => void,
  selectedIndex?: number | null
): Highcharts.Options => {
  return {
    chart: {
      type: "column",
      height: 350
    },
    title: {
      text: ""
    },
    credits: {
      enabled: false
    },
    xAxis: {
      categories: categories,
      labels: {
        rotation: viewType === "daily" ? -45 : 0,
        style: {
          fontSize: "11px"
        }
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: "Count"
      },
      stackLabels: {
        enabled: false
      }
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      backgroundColor: "white",
      borderColor: "#CCC",
      borderWidth: 0,
      shadow: false
    },
    tooltip: {
      backgroundColor: "#42405A",
      borderColor: "#42405A",
      borderRadius: 5,
      shadow: false,
      style: {
        color: "#ffffff",
        fontSize: "12px"
      },
      headerFormat: "<b>{point.x}</b><br/>",
      pointFormat:
        "{series.name}: <b>{point.y}</b><br/>" +
        "Total: <b>{point.stackTotal}</b>"
    },
    plotOptions: {
      column: {
        stacking: "normal",
        cursor: "pointer",
        dataLabels: {
          enabled: false
        },
        states: {
          hover: {
            brightness: 0.1,
            enabled: true
          }
        },
        point: {
          events: {
            click: function () {
              if (onPointClick) {
                onPointClick(this.index, this.series.name)
              }
            }
          }
        }
      }
    },
    series: series.map(s => ({
      name: s.name,
      type: "column" as const,
      data: s.data.map((value, index) => {
        const isSelected = selectedIndex === index
        return {
          y: value,
          // Apply brighter color when selected
          color: isSelected ? adjustBrightness(s.color, 30) : undefined
        }
      }),
      color: s.color
    })),
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600
          },
          chartOptions: {
            legend: {
              enabled: true
            },
            xAxis: {
              labels: {
                rotation: -45
              }
            }
          }
        }
      ]
    }
  }
}

// Helper function to adjust color brightness
function adjustBrightness(hex: string, percent: number): string {
  // Remove # if present
  const color = hex.replace("#", "")

  // Convert to RGB
  const r = parseInt(color.substring(0, 2), 16)
  const g = parseInt(color.substring(2, 4), 16)
  const b = parseInt(color.substring(4, 6), 16)

  // Adjust brightness
  const adjust = (val: number) => {
    const adjusted = val + (255 - val) * (percent / 100)
    return Math.min(255, Math.max(0, Math.round(adjusted)))
  }

  const newR = adjust(r)
  const newG = adjust(g)
  const newB = adjust(b)

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, "0")}${newG
    .toString(16)
    .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`
}
